import { useContext, createContext, useMemo } from 'react';
import { useQuery as useApolloQuery, useMutation as useApolloMutation, useLazyQuery as useLazyApolloQuery, useSuspenseQuery as useApolloSuspenseQuery, useBackgroundQuery as useApolloBackgroundQuery, useReadQuery as useApolloReadQuery, useQueryRefHandlers, useLoadableQuery, ApolloLink, ApolloClient, InMemoryCache, fromPromise, Observable } from '@apollo/client';
import { createController } from './entityManagement/controller/controller';
import http from 'hub-http/clients/apiClient';
import { maybeCreateDevtools } from './entityManagement/devtools/maybeCreateDevtools';
import { jsx as _jsx } from "react/jsx-runtime";
export const contextKey = Symbol.for('__DATA_FETCHING_CLIENT_CONTEXT__');

// Memoized context creation using the `createContext` function as a cache. Following a pattern from Apollo Client
function getDataFetchingClientContext() {
  let context = createContext[contextKey];
  if (!context) {
    context = /*#__PURE__*/createContext({});
    Object.defineProperty(createContext, contextKey, {
      value: context,
      enumerable: false,
      writable: false,
      configurable: true
    });
    context.displayName = 'DataFetchingClientContext';
  }
  return context;
}
function useDataFetchingClient() {
  const {
    client
  } = useContext(getDataFetchingClientContext());

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore process is declared in webpack-env, but doesn't impact this module's type externally so we can ignore in downstream builds
  if (process.env.NODE_ENV !== 'production') {
    if (!client) {
      throw new Error('Cannot find context. Did you forget to configure `DataFetchingClientProvider`');
    }
  }
  return client;
}
function DataFetchingClientProvider({
  client,
  children
}) {
  const clientValue = useMemo(() => ({
    client
  }), [client]);
  const DataFetchingClientContext = getDataFetchingClientContext();
  return /*#__PURE__*/_jsx(DataFetchingClientContext.Provider, {
    value: clientValue,
    children: children
  });
}

// See https://spec.graphql.org/June2018/#sec-Names
const isValidGraphQLName = str => typeof str === 'string' && /^[_A-Za-z][_0-9A-Za-z]+$/.test(str);
const registeredQueries = new WeakMap();
const getFieldName = operation => {
  const registeredOperation = registeredQueries.get(operation);
  if (!registeredOperation) {
    throw new Error('`data-fetching-client` only supports queries created with `registerQuery` or `registerMutation`.');
  }
  const fieldName = registeredOperation.fieldName;
  return fieldName;
};
class DataFetchingClientLink extends ApolloLink {
  constructor({
    client
  }) {
    super();
    this.client = client;
  }
  request({
    variables,
    query,
    getContext
  }) {
    const context = getContext();
    const fieldDef = registeredQueries.get(query);
    if (!fieldDef) {
      throw new Error('`data-fetching-client` only supports queries created with `registerQuery` or `registerMutation`.');
    }
    const {
      fetcher,
      fieldName,
      withResultAlias
    } = fieldDef;
    return fromPromise(new Promise((resolve, reject) => {
      try {
        resolve(fetcher(variables, Object.assign({}, context, {
          client: this.client
        })));
      } catch (error) {
        reject(error);
      }
    })).map(result => ({
      data: {
        [withResultAlias ? 'result' : fieldName]: result
      }
    }));
  }
}
function createFetcherRejectionLink(onFetcherRejection) {
  return new ApolloLink((operation, forward) => {
    return new Observable(observer => {
      const sub = forward(operation).subscribe({
        next(result) {
          observer.next(result);
        },
        error(err) {
          observer.error(err);
          onFetcherRejection(err, getOperationInfo(operation));
        },
        complete() {
          observer.complete();
        }
      });
      return () => {
        sub.unsubscribe();
      };
    });
  });
}
function getOperationInfo(operation) {
  const operationDefinition = operation.query.definitions.find(def => def.kind === 'OperationDefinition');
  if (!operationDefinition || !(operationDefinition.operation === 'query' || operationDefinition.operation === 'mutation')) {
    throw new Error('Could not determine operation info');
  }
  return {
    operationType: operationDefinition.operation,
    fieldName: getFieldName(operation.query),
    variables: operation.variables
  };
}
const defaultEntityManagementOptions = {
  httpClient: http,
  globalDecorators: [],
  createDevtools: maybeCreateDevtools
};
class DataFetchingClient extends ApolloClient {
  constructor({
    queryFieldPolicies,
    mutationFieldPolicies,
    assumeImmutableResults,
    defaultOptions,
    connectToDevTools,
    onFetcherRejection,
    unstable__enableEntityManagement
  } = {}) {
    super({
      defaultOptions,
      cache: new InMemoryCache({
        typePolicies: {
          Query: {
            fields: Object.assign({}, queryFieldPolicies)
          },
          Mutation: {
            fields: Object.assign({}, mutationFieldPolicies)
          }
        },
        addTypename: false
      }),
      assumeImmutableResults,
      connectToDevTools
    });
    if (unstable__enableEntityManagement) {
      this.entityController = createController(Object.assign({}, defaultEntityManagementOptions, unstable__enableEntityManagement === true ? {} : unstable__enableEntityManagement));
    }
    this.setLink(onFetcherRejection ? ApolloLink.from([createFetcherRejectionLink(onFetcherRejection), new DataFetchingClientLink({
      client: this
    })]) : new DataFetchingClientLink({
      client: this
    }));

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore process is declared in webpack-env, but doesn't impact this module's type externally so we can ignore in downstream builds
    if (process.env.NODE_ENV !== 'production') {
      window.__DATA_FETCHING_CLIENT__ = this;
    }
  }
}
function registerOperation(operation, {
  fieldName,
  args = [],
  fetcher
}, withResultAlias) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore process is declared in webpack-env, but doesn't impact this module's type externally so we can ignore in downstream builds
  if (process.env.NODE_ENV !== 'production') {
    if (!isValidGraphQLName(fieldName)) {
      throw new Error(`"${fieldName}" is not a valid GraphQL Name and cannot be used as \`fieldName\`. https://spec.graphql.org/June2018/#sec-Names`);
    }
    args.forEach(arg => {
      if (!isValidGraphQLName(arg)) {
        throw new Error(`"${String(arg)}" is not a valid GraphQL Name and cannot be used in \`args\`. https://spec.graphql.org/June2018/#sec-Names`);
      }
    });
  }
  const query = {
    kind: 'Document',
    definitions: [{
      kind: 'OperationDefinition',
      operation,
      name: {
        kind: 'Name',
        value: fieldName
      },
      variableDefinitions: args.map(arg => ({
        kind: 'VariableDefinition',
        variable: {
          kind: 'Variable',
          name: {
            kind: 'Name',
            value: arg
          }
        },
        type: {
          kind: 'NamedType',
          name: {
            kind: 'Name',
            value: 'JSON'
          }
        }
      })),
      selectionSet: {
        kind: 'SelectionSet',
        selections: [{
          kind: 'Field',
          name: {
            kind: 'Name',
            value: fieldName
          },
          alias: withResultAlias ? {
            kind: 'Name',
            value: 'result'
          } : undefined,
          directives: typeof fetcher === 'undefined' ? [{
            kind: 'Directive',
            name: {
              kind: 'Name',
              value: 'client'
            }
          }] : [],
          arguments: args.map(arg => ({
            kind: 'Argument',
            name: {
              kind: 'Name',
              value: arg
            },
            value: {
              kind: 'Variable',
              name: {
                kind: 'Name',
                value: arg
              }
            }
          }))
        }]
      }
    }]
  };
  registeredQueries.set(query, {
    fetcher,
    withResultAlias,
    fieldName
  });
  return query;
}
function registerQuery(options) {
  return registerOperation('query', options, false);
}
function registerMutation(options) {
  return registerOperation('mutation', options, false);
}
function useQuery(query, options) {
  const {
    client
  } = useContext(getDataFetchingClientContext());
  return useApolloQuery(query, Object.assign({
    client
  }, options));
}
function useLazyQuery(query, options) {
  const {
    client
  } = useContext(getDataFetchingClientContext());
  return useLazyApolloQuery(query, Object.assign({
    client
  }, options));
}
function useMutation(query, options) {
  const {
    client
  } = useContext(getDataFetchingClientContext());
  return useApolloMutation(query, Object.assign({
    client
  }, options));
}
function useSuspenseQuery(query, options) {
  const client = useDataFetchingClient();
  return useApolloSuspenseQuery(query, Object.assign({
    client
  }, options));
}
function useBackgroundQuery(query, options) {
  const client = useDataFetchingClient();
  return useApolloBackgroundQuery(query, Object.assign({
    client
  }, options));
}
function useReadQuery(queryRef) {
  if (!queryRef) {
    throw new Error('useReadQuery must be called with a query ref. If you are using `skip`, this component should be conditionally rendered.');
  }
  return useApolloReadQuery(queryRef);
}
export { getDataFetchingClientContext, DataFetchingClientProvider, DataFetchingClient, registerQuery, registerMutation, registerOperation, useDataFetchingClient, useQuery, useLazyQuery, useMutation, useSuspenseQuery, useBackgroundQuery, useReadQuery, useQueryRefHandlers, useLoadableQuery, getFieldName };